import { Component, ViewChild, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import * as _ from 'lodash';
import { CommonFunctions } from '../static/common-functions';
import { BaseBus } from '../models/BaseBus';
import { GlobalsService } from '../services/globals.service';
import { UntypedFormGroup } from '@angular/forms';
import { DQOIReferenceData } from '../models/DQOIReferenceData';
import { BaseDQOIComponent } from './base-dqoi.component';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { CRUDActions } from '../enums/CRUDActions';
import { DQOIReferenceDataQuery } from '@app/cp/dqoi/state/dqoi-reference-data.query';
import { MessageType } from '../enums/MessageType';
import { Message } from '../models/message';
import { SaveMode } from '../enums/SaveMode';
import { ConfirmOutput } from '../models/ConfirmOutput';
import { CPDialogResult } from '../enums/CPDialogResult';
import { CommonParam } from '../models/CommonParam';
import { DateFunctions } from '../static/date-functions';
import { BusinessObjectFactory } from '../models/BusinessObjectFactory';
import { MessageInfoDialogComponent } from '../components/message-info-dialog/message-info-dialog.component';
import { BaseMessageInfo } from '../models/BaseMessageInfo';
import { MessageInfoType } from '../enums/MessageInfoType';
import { SelectionEvent } from '@progress/kendo-angular-grid';
import { ConfirmDialogComponent } from '../components/confirm-dialog/confirm-dialog.component';
import { Subscription } from 'rxjs';
import { AppClaims } from '../models/AppClaims';
import { validateOverridables, EmptyOverridable } from '../decorators/overridable/base-method-override-decorator';
import { CommonValues } from '../static/CommonValues';

@Component({
    selector: '',
    template: '',
    standalone: false
})
export class BaseAppEditComponent<T extends BaseBus, S>  implements OnDestroy {

    constructor(public globals: GlobalsService, qry_rd: DQOIReferenceDataQuery) {
        this.qry_rd = qry_rd;
        validateOverridables(this, Object.getPrototypeOf(this).constructor.name, BaseAppEditComponent);
    }

    @EmptyOverridable()
    createForm() { }

    @EmptyOverridable()
    setForm() { }

    @EmptyOverridable()
    setNewDefaults() { }

    @EmptyOverridable()
    getData() { }

    @EmptyOverridable()
    afterDataSet() { }

    @EmptyOverridable()
    saveAll() { }

    @EmptyOverridable()
    saveIndividual() { }

    @EmptyOverridable()
    afterSave(flag: boolean) { }

    @EmptyOverridable()
    confirmCustom(data: ConfirmOutput) { }

    @EmptyOverridable()
    parentMessage(e: Message) {}

    @ViewChild('miDialog') protected miDialog: MessageInfoDialogComponent;
    @ViewChild('cnfDialog') protected cnfDialog: ConfirmDialogComponent;
    className: string;
    appCode: string = null;
    subs_data: Subscription;
    private _rights: AppClaims | null = null;
    virtualizationSettings = CommonValues.virtualizationSettings;

    /**
     * Using this getter can cause race conditions in code depending on how this is used.
     * While we have a user data  resolver in place to help, this still could cause issues.
     * If you need reliable data from the user object, use CmProAuthService.subscribeToLoginEvents and CmProAuthService.unsubscribeToLoginEvents in your component
     *
     * @readonly
     * @type {AppClaims}
     * @memberof BaseAppEditComponent
     */
    get rights(): AppClaims {
        return this._rights ?? this.globals.user?.appClaims ?? new AppClaims();
    }

    set rights(value: AppClaims) {
        this._rights = value;
    }

    svc_data: S;
    dataLoading: boolean = false;
    selectedData: T;
    originalData: T;
    qry_rd: DQOIReferenceDataQuery;
    get referenceData(): DQOIReferenceData {
        return BaseDQOIComponent._referenceData;
    }
    get hasReferenceData(): boolean {
        let retVal: boolean = false;
        try {
            retVal = this.referenceData !== undefined && this.referenceData !== null && CommonFunctions.hasArrayElementValue(this.referenceData.dispatchStatus) ? true : false;
        }
        catch {
            retVal = false;
        }
        return retVal;

    }
    subs_dataRef: Subscription;

    id: any;
    public editForm: UntypedFormGroup;
    readOnly: boolean = true;
    @Input() saveMode: SaveMode = SaveMode.all;
    @Output() closed: EventEmitter<any> = new EventEmitter<any>();
    @Output() parentMessageOut: EventEmitter<Message> = new EventEmitter<Message>();

    sendParentMessage(type: MessageType, subject: string = null, message: string = null, values: any = null, value: any = null) {
        let msg: Message = new Message();
        msg.messageType = type;
        msg.messageSubject = subject;
        msg.message = message;
        msg.values = values;
        msg.value = value;
        this.parentMessageOut.emit(msg);
    }

    subs_save: Subscription;
    ngOnDestroy() {
        CommonFunctions.unsubscribe(this.subs_save);
        CommonFunctions.unsubscribe(this.subs_dataRef);
    }
    rowSelected(e: SelectionEvent) {
        if (e.selectedRows.length > 0) {
            this.selectedData = e.selectedRows[0].dataItem;
        }
    }

    public filterSettings: DropDownFilterSettings = {
        caseSensitive: false,
        operator: 'contains'
    };
    setDirty() {
        if (this.hasEditForm) {
            this.editForm.markAsDirty();
        }
    }
    setPristine() {
        if (this.hasEditForm) {
            this.editForm.markAsPristine();
        }
    }
    get hasEditForm(): boolean {
        return this.editForm !== undefined && this.editForm !== null ? true : false;
    }
    get hasData(): boolean {
        return CommonFunctions.hasValue(this.selectedData);
    }
    get canEdit(): boolean {
        return this.readOnly && this.hasData;
    }
    get canClear(): boolean {
        return this.readOnly && this.hasData;
    }
    get isDirty(): boolean {
        return !this.readOnly && this.hasEditForm && this.editForm.dirty;
    }


    private _canSaveValue: boolean = false;
    private updateCanSaveValue(): void {
        let okToSave: boolean = false;
        if (this.isDirty) {
            if (this.hasId) {
                okToSave = this.editForm.valid && this.customFormValidation();
            }
            else {
                okToSave = this.editForm.valid && this.customFormValidation();
            }
        }
        this._canSaveValue = okToSave || this._hasSaveError;
    }

    get canSave(): boolean {
        this.updateCanSaveValue();
        return this._canSaveValue;
    }

    /**
     * This is a property that is used to determine if the save operation has encountered an error.
     * This will control if the setPristine() method is called after the save operation.
     *
     * @protected
     * @type {boolean}
     * @memberof BaseAppMComponent
     */
    protected set hasSaveError(value: boolean) {
        if (this._hasSaveError !== value) {
            this._hasSaveError = value;
            this.updateCanSaveValue();
        }
    }
    private _hasSaveError: boolean = false;

    get hasId(): boolean {
        return this.hasData && this.selectedData.hasId ? true : false;
    }
    customFormValidation() {
        return this.selectedData.isValid();
    }

    new() {
        this.clear();
        this.setNewDefaults();
        this.setData(this.selectedData);
        this.edit();
    }

    edit() {
        this.readOnly = false;
    }
    protected setNewObjDefaults(busObj: T) {
        busObj.setNew(this.globals.userId, DateFunctions.getDateNow(), this.globals.companyId, this.globals.personId);
    }
    createNew(busObj: T): T {
        let obj: T = BusinessObjectFactory.Create(this.className, busObj);
        if (obj.initialize) {
            obj.initialize(busObj);
            this.setNewObjDefaults(obj);
        }
        return obj;
    }

    setData(data: T) {
        this.selectedData = this.createNew(data);
        this.id = this.selectedData.CID();
        this.originalData = _.cloneDeep(data);
        this.afterDataSet();
        this.createForm();
        this.setForm();
    }

    getDataForCancel() {
        return this.createNew(this.originalData);
    }
    cancelEdit() {
        this.selectedData = this.getDataForCancel();
        if (!this.selectedData.hasId) {
        }
        this.setForm();
        this.readOnly = true;
    }
    clear() {
        this.selectedData = this.createNew(null);
        this.originalData = this.createNew(null);
        this.id = null;
        this.readOnly = true;
        if (this.hasEditForm) {
            this.editForm.reset();
        }

    }
    close(data: any = null, clear: boolean = true) {
        if (clear) {
            this.clear();
        }
        this.closed.emit(data);
    }
    save() {
        if (this.saveMode === SaveMode.all) {
            this.saveAll();
        }
        else {
            this.saveIndividual();
        }
    }

    saveSuccess(message: string = null, prefix: string = null) {
        prefix = CommonFunctions.getStringValueIfNull(prefix, this.appCode);
        let not: string = CommonFunctions.hasStringValue(message) ? message : (prefix + " Successfully Saved");
        this.globals.notifySuccess(not);
        this.afterSave(true);
    }
    saveError(message: string = null) {
        let not: string = "There was a problem saving " + this.appCode + (CommonFunctions.hasStringValue(message) ? " " + message : "");
        this.globals.notifyError(not);
        this.afterSave(false);
    }
    cnfShow(message: string, action: CRUDActions = CRUDActions.None, data: any = null, title: string = null, code: string = null, okButtonOnly: boolean = false, yesNo:boolean=false) {
        if (CommonFunctions.hasValue(this.cnfDialog)) {
            this.cnfDialog.show(message, action, data, title, code, okButtonOnly, yesNo);
        }
    }
    cnfShowNote(note: string, title: string = null, width: number = null) {
        this.ctrlOpenFlag = true;
        if (CommonFunctions.hasValue(this.cnfDialog)) {
            this.cnfDialog.showNote(note, title, width);
        }
    }
    closeCtrlOpenFlag: boolean = false;
    ctrlOpenFlag: boolean = false;
    confirmDialog(data: ConfirmOutput) {
        this.closeCtrlOpenFlag = true;
        let handled: boolean = false;
        if (data.dialogResult === CPDialogResult.ok) {
            switch (data.action) {
                case CRUDActions.Delete:
                    handled = true;
                    break;
            }
        }
        if (!handled) {
            this.confirmCustom(data);
        }
    }

    seParam: CommonParam = new CommonParam();
    addParamValue(key: string, value: any) {
        this.seParam.addParam(key, value);
    }

    miShow(message: string, info: BaseMessageInfo, data: any = null, title: string = null, code: string = null, okButtonText: string = "Ok", type: MessageInfoType = MessageInfoType.general) {
        if (CommonFunctions.hasValue(this.miDialog)) {
            this.miDialog.show(message, info, data, title, code, okButtonText, type);
        }
    }
}

